import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { LoaderCircle, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supabase } from "@/lib/supabase";

type AuthorizationDetails = {
  authorization_id: string;
  redirect_uri: string;
  client: {
    id: string;
    name: string;
    uri: string;
    logo_uri: string;
  };
  user: {
    id: string;
    email: string;
  };
  scope: string;
};

function redirectHost(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

export function OAuthConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get("authorization_id");
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<"approve" | "deny" | null>(null);

  const scopes = useMemo(
    () => details?.scope.split(/\s+/).filter(Boolean) ?? [],
    [details?.scope],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadAuthorization() {
      if (!authorizationId) {
        setError(
          "This approval link is incomplete. Return to the app and try connecting again.",
        );
        return;
      }

      const { data: sessionData, error: sessionError } = await supabase.auth
        .getSession();
      if (cancelled) return;

      if (sessionError) {
        setError(sessionError.message);
        return;
      }

      if (!sessionData.session) {
        const returnTo = `/oauth/consent?${searchParams.toString()}`;
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        });
        return;
      }

      const { data, error: detailsError } = await supabase.auth.oauth
        .getAuthorizationDetails(authorizationId);
      if (cancelled) return;

      if (detailsError || !data) {
        setError(
          detailsError?.message ??
            "CC OS could not load this approval request.",
        );
        return;
      }

      if ("redirect_url" in data) {
        window.location.assign(data.redirect_url);
        return;
      }

      setDetails(data);
    }

    void loadAuthorization();
    return () => {
      cancelled = true;
    };
  }, [authorizationId, navigate, searchParams]);

  async function submitDecision(action: "approve" | "deny") {
    if (!details || decision) return;

    setDecision(action);
    setError(null);

    const result = action === "approve"
      ? await supabase.auth.oauth.approveAuthorization(
        details.authorization_id,
        {
          skipBrowserRedirect: true,
        },
      )
      : await supabase.auth.oauth.denyAuthorization(details.authorization_id, {
        skipBrowserRedirect: true,
      });

    if (result.error || !result.data) {
      setError(result.error?.message ?? "CC OS could not save your choice.");
      setDecision(null);
      return;
    }

    window.location.assign(result.data.redirect_url);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="space-y-1.5">
            <CardTitle>Connect to CC OS</CardTitle>
            <CardDescription>
              Review this request before allowing another app to use your CC OS
              account.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {error && (
            <div
              className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
              role="alert"
            >
              {error}
            </div>
          )}

          {!details && !error && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <LoaderCircle
                className="h-4 w-4 animate-spin"
                aria-hidden="true"
              />
              Loading approval request…
            </div>
          )}

          {details && (
            <>
              <div className="rounded-lg border bg-background p-4">
                <p className="text-sm text-muted-foreground">
                  App requesting access
                </p>
                <p className="mt-1 font-semibold">
                  {details.client.name || "External application"}
                </p>
                <p className="mt-1 break-all text-xs text-muted-foreground">
                  Returns to {redirectHost(details.redirect_uri)}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">What this allows</p>
                <p className="text-sm text-muted-foreground">
                  This app can use CC OS actions allowed by your existing staff
                  role. It cannot bypass CC OS permissions.
                </p>
                {scopes.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {scopes.map((scope) => (
                      <span
                        key={scope}
                        className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Signed in as {details.user.email}
              </p>
            </>
          )}
        </CardContent>

        {details && (
          <CardFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={decision !== null}
              onClick={() => void submitDecision("deny")}
            >
              {decision === "deny" ? "Cancelling…" : "Cancel"}
            </Button>
            <Button
              type="button"
              disabled={decision !== null}
              onClick={() => void submitDecision("approve")}
            >
              {decision === "approve" ? "Connecting…" : "Allow access"}
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
